
import { useMemo } from 'react';

interface ROIBreakdownProps {
  currentCost: number;
  mtaCost: number;
  timeSavedHours: number;
  extraJobs: number;
  extraRevenue: number;
  hourlyRate: number;
}

export default function ROIBreakdown({
  currentCost,
  mtaCost,
  timeSavedHours,
  extraJobs,
  extraRevenue,
  hourlyRate,
}: ROIBreakdownProps) {
  const timeSavingsValue = timeSavedHours * hourlyRate;
  const totalBenefit = extraRevenue + timeSavingsValue;
  const netROI = totalBenefit - mtaCost;
  const roiMultiplier = mtaCost > 0 ? Math.round(totalBenefit / mtaCost) : 0;

  const bars = useMemo(
    () => [
      {
        label: 'Extra Revenue',
        value: extraRevenue,
        maxValue: totalBenefit,
        color: 'from-amber-400 to-orange-500',
        icon: 'ri-money-dollar-circle-line',
      },
      {
        label: 'Time Savings Value',
        value: timeSavingsValue,
        maxValue: totalBenefit,
        color: 'from-emerald-400 to-emerald-600',
        icon: 'ri-time-line',
      },
      {
        label: 'MtaPlannerPro Cost',
        value: mtaCost,
        maxValue: totalBenefit,
        color: 'from-slate-400 to-slate-500',
        icon: 'ri-subtract-line',
      },
    ],
    [extraRevenue, timeSavingsValue, mtaCost, totalBenefit],
  );

  return (
    <div className="space-y-6">
      {/* ROI Multiplier Badge */}
      <div className="flex items-center justify-between">
        <h4 className="text-white font-bold text-lg">Monthly ROI Breakdown</h4>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full">
          <i className="ri-arrow-up-circle-fill text-emerald-400 text-sm"></i>
          <span className="text-emerald-400 text-sm font-bold">{roiMultiplier}x Return</span>
        </div>
      </div>

      {/* Bars */}
      <div className="space-y-4">
        {bars.map((bar, i) => {
          const width = bar.maxValue > 0 ? Math.max((bar.value / bar.maxValue) * 100, 4) : 4;
          return (
            <div key={i}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <i className={`${bar.icon} text-slate-400 text-sm`}></i>
                  <span className="text-slate-300 text-sm font-medium">{bar.label}</span>
                </div>
                <span className="text-white font-bold text-sm tabular-nums">
                  ${bar.value.toLocaleString()}/mo
                </span>
              </div>
              <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${bar.color} rounded-full transition-all duration-700 ease-out`}
                  style={{ width: `${width}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Net Profit */}
      <div className="border-t border-white/10 pt-4">
        <div className="flex items-center justify-between">
          <span className="text-slate-300 font-semibold">Net Monthly Profit</span>
          <span className="text-2xl font-bold text-emerald-400 tabular-nums">
            +${netROI.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}
